import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { doctorPatientPath, ROUTES } from '../../constants/routes';
import {
  doctorCreatePatient,
  doctorGetPatient,
  doctorUpdatePatient,
} from '../services/doctorApi';
import { useDoctorSession } from '../contexts/DoctorSessionContext';

const emptyMed = () => ({ name: '', dose: '', frequency: '' });

const inputClass =
  'w-full rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20';

function Field({ label, required, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-semibold text-gray-600">
        {label}
        {required ? <span className="text-critical"> *</span> : null}
      </span>
      {children}
    </label>
  );
}

export default function DoctorPatientFormScreen({ mode = 'create' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { showToast } = useDoctorSession();
  const isEdit = mode === 'edit';
  const [form, setForm] = useState({
    full_name: '',
    age: '',
    gender: '',
    phone: '',
    medical_record_number: '',
    known_conditions: '',
    allergies: '',
    current_medications: [emptyMed()],
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isEdit || !id) return undefined;
    let cancelled = false;
    doctorGetPatient(id)
      .then((p) => {
        if (cancelled) return;
        setForm({
          full_name: p.full_name || '',
          age: p.age != null ? String(p.age) : '',
          gender: p.gender || '',
          phone: p.phone || '',
          medical_record_number: p.medical_record_number || '',
          known_conditions: p.known_conditions || '',
          allergies: p.allergies || '',
          current_medications:
            p.current_medications?.length > 0
              ? p.current_medications.map((m) => ({
                  name: m.name || '',
                  dose: m.dose || '',
                  frequency: m.frequency || '',
                }))
              : [emptyMed()],
        });
      })
      .catch((err) => setError(err.message));
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const updateMed = (index, key, value) => {
    setForm((f) => {
      const meds = [...f.current_medications];
      meds[index] = { ...meds[index], [key]: value };
      return { ...f, current_medications: meds };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const payload = {
      full_name: form.full_name.trim(),
      age: form.age === '' ? null : Number(form.age),
      gender: form.gender || null,
      phone: form.phone.trim() || null,
      medical_record_number: form.medical_record_number.trim() || null,
      known_conditions: form.known_conditions.trim() || null,
      allergies: form.allergies.trim() || null,
      current_medications: form.current_medications.filter((m) => m.name.trim()),
    };
    try {
      if (isEdit) {
        await doctorUpdatePatient(id, payload);
        showToast(t('doctorPatientUpdated'));
        navigate(doctorPatientPath(id), { replace: true });
      } else {
        const created = await doctorCreatePatient(payload);
        showToast(t('doctorPatientCreated'));
        navigate(doctorPatientPath(created.id), { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-page">
      <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-gray-200 bg-surface px-4 py-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex h-10 w-10 items-center justify-center rounded-full text-gray-600"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-bold text-slate-900">
          {isEdit ? t('doctorEditPatient') : t('doctorAddPatient')}
        </h1>
      </header>

      <form onSubmit={handleSubmit} className="space-y-4 px-4 py-4 pb-28">
        {error && (
          <div className="rounded-xl bg-critical-light px-4 py-3 text-sm text-critical-dark">
            {error}
          </div>
        )}

        <Field label={t('doctorFullName')} required>
          <input
            required
            value={form.full_name}
            onChange={set('full_name')}
            className={inputClass}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('doctorAge')}>
            <input
              type="number"
              min={0}
              value={form.age}
              onChange={set('age')}
              className={inputClass}
            />
          </Field>
          <Field label={t('doctorGender')}>
            <select value={form.gender} onChange={set('gender')} className={inputClass}>
              <option value="">{t('doctorSelectGender')}</option>
              <option value="Male">{t('doctorMale')}</option>
              <option value="Female">{t('doctorFemale')}</option>
              <option value="Other">{t('doctorOther')}</option>
            </select>
          </Field>
        </div>

        <Field label={t('doctorPhone')}>
          <input
            value={form.phone}
            onChange={set('phone')}
            placeholder={t('doctorOptional')}
            className={inputClass}
          />
        </Field>

        <Field label={t('doctorMRN')}>
          <input
            value={form.medical_record_number}
            onChange={set('medical_record_number')}
            placeholder={t('doctorOptional')}
            className={inputClass}
          />
        </Field>

        <Field label={t('doctorKnownConditions')}>
          <textarea
            value={form.known_conditions}
            onChange={set('known_conditions')}
            placeholder={t('doctorOptional')}
            rows={2}
            className={inputClass}
          />
        </Field>

        <Field label={t('doctorAllergies')}>
          <textarea
            value={form.allergies}
            onChange={set('allergies')}
            placeholder={t('doctorOptional')}
            rows={2}
            className={inputClass}
          />
        </Field>

        <div className="rounded-xl bg-white p-4 ring-1 ring-gray-100">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-900">{t('doctorCurrentMeds')}</p>
            <button
              type="button"
              onClick={() =>
                setForm((f) => ({
                  ...f,
                  current_medications: [...f.current_medications, emptyMed()],
                }))
              }
              className="flex items-center gap-1 text-sm font-medium text-primary"
            >
              <Plus className="h-4 w-4" /> {t('add')}
            </button>
          </div>
          <div className="space-y-3">
            {form.current_medications.map((med, index) => (
              <div key={index} className="space-y-2 rounded-lg bg-gray-50 p-3">
                <div className="flex items-end gap-2">
                  <label className="block min-w-0 flex-1 space-y-1.5">
                    <span className="text-xs font-semibold text-gray-600">{t('doctorMedName')}</span>
                    <input
                      value={med.name}
                      onChange={(e) => updateMed(index, 'name', e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </label>
                  {form.current_medications.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          current_medications: f.current_medications.filter((_, i) => i !== index),
                        }))
                      }
                      className="mb-2 text-critical"
                      aria-label={t('cancel')}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="block space-y-1.5">
                    <span className="text-xs font-semibold text-gray-600">{t('doctorDose')}</span>
                    <input
                      value={med.dose}
                      onChange={(e) => updateMed(index, 'dose', e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="block space-y-1.5">
                    <span className="text-xs font-semibold text-gray-600">{t('doctorFrequency')}</span>
                    <input
                      value={med.frequency}
                      onChange={(e) => updateMed(index, 'frequency', e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                    />
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button type="submit" disabled={loading} className="btn-primary disabled:opacity-60">
          {loading ? '…' : t('save')}
        </button>
        {!isEdit && (
          <button
            type="button"
            onClick={() => navigate(ROUTES.DOCTOR_PATIENTS)}
            className="w-full py-2 text-sm text-gray-500"
          >
            {t('cancel')}
          </button>
        )}
      </form>
    </div>
  );
}
